"use client";

import { useDeferredValue, useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";

import {
  Button,
  Field,
  FieldError,
  FieldLabel,
  Input,
  NativeSelect,
} from "@stocks/ui";
import type {
  BomResponse,
  ProductOptionResponse,
  SaveBomRequest,
} from "@stocks/shared";

import { ITEM_CATEGORY_LABELS, useItemOptions } from "@/entities/item";

import { useSaveBom } from "../model/useSaveBom";

interface LineValues {
  optionId: string;
  itemId: string;
  /** 선택한 자재 표시용 (검색 결과에서 벗어나도 선택지가 유지되도록) */
  itemLabel: string;
  baseUnit: string;
  quantityPerUnit: string;
  memo: string;
}

interface Values {
  itemSearch: string;
  note: string;
  lines: LineValues[];
}

const EMPTY_LINE: LineValues = {
  optionId: "",
  itemId: "",
  itemLabel: "",
  baseUnit: "",
  quantityPerUnit: "",
  memo: "",
};

const toValues = (bom: BomResponse | null | undefined): Values => ({
  itemSearch: "",
  note: "",
  lines: (bom?.lines ?? []).map((l) => ({
    optionId: l.optionId ?? "",
    itemId: l.itemId,
    itemLabel: `${l.itemCode} · ${l.itemName}`,
    baseUnit: l.baseUnit,
    quantityPerUnit: String(Number(l.quantityPerUnit)),
    memo: l.memo ?? "",
  })),
});

/**
 * 제품 BOM 편집 — 옵션(색상×사이즈)별 자재 선택 + 한 벌당 기준 소요량 입력.
 * 옵션을 비우면 모든 옵션 공통. 저장하면 새 버전이 만들어진다.
 */
export const BomEditor = ({
  productId,
  options,
  bom,
}: {
  productId: string;
  options: ProductOptionResponse[];
  bom: BomResponse | null | undefined;
}) => {
  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<Values>({ defaultValues: toValues(bom) });
  const { fields, append, remove } = useFieldArray({ control, name: "lines" });
  const save = useSaveBom(productId);

  // 저장·재조회 결과(새 버전)로 폼을 맞춘다
  useEffect(() => reset(toValues(bom)), [bom, reset]);

  const search = useDeferredValue(watch("itemSearch").trim());
  const { data: itemPage } = useItemOptions(search);
  const items = itemPage?.items ?? [];
  const lines = watch("lines");

  const onSubmit = (v: Values) =>
    save.mutate({
      note: v.note.trim() || undefined,
      lines: v.lines.map(
        (l): SaveBomRequest["lines"][number] => ({
          optionId: l.optionId || undefined,
          itemId: l.itemId,
          quantityPerUnit: Number(l.quantityPerUnit),
          memo: l.memo.trim() || undefined,
        }),
      ),
    });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4'>
      <p className='text-sm text-muted-foreground'>
        {bom
          ? `현재 BOM 버전 v${bom.version} — 저장하면 새 버전이 만들어지고 기존 발주는 고정된 버전을 유지합니다.`
          : "등록된 BOM 이 없습니다. 자재와 한 벌당 소요량을 입력해 저장하세요."}
      </p>

      <Field className='max-w-sm'>
        <FieldLabel htmlFor='bom-item-search'>자재 검색</FieldLabel>
        <Input
          id='bom-item-search'
          placeholder='이름 / 코드 / 규격'
          {...register("itemSearch")}
        />
      </Field>

      <div className='flex flex-col gap-3'>
        {fields.map((f, i) => {
          const current = lines[i];
          const hasCurrent =
            current?.itemId && !items.some((it) => it.id === current.itemId);
          return (
            <div
              key={f.id}
              className='grid items-start gap-2 rounded-md border p-3 lg:grid-cols-[10rem_1fr_9rem_1fr_auto]'
            >
              <Field>
                <FieldLabel htmlFor={`bom-option-${i}`}>적용 옵션</FieldLabel>
                <NativeSelect
                  id={`bom-option-${i}`}
                  className='w-full'
                  {...register(`lines.${i}.optionId`)}
                >
                  <option value=''>전체 공통</option>
                  {options.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.color} / {o.size}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field>
                <FieldLabel htmlFor={`bom-item-${i}`}>자재</FieldLabel>
                <NativeSelect
                  id={`bom-item-${i}`}
                  className='w-full'
                  {...register(`lines.${i}.itemId`, {
                    required: "자재를 선택하세요.",
                    onChange: (e) => {
                      const it = items.find((x) => x.id === e.target.value);
                      setValue(`lines.${i}.itemLabel`, it ? `${it.code} · ${it.name}` : "");
                      setValue(`lines.${i}.baseUnit`, it?.baseUnit ?? "");
                    },
                  })}
                >
                  <option value=''>자재 선택</option>
                  {hasCurrent && (
                    <option value={current.itemId}>{current.itemLabel}</option>
                  )}
                  {items.map((it) => (
                    <option key={it.id} value={it.id}>
                      {it.code} · {it.name}
                      {it.category ? ` (${ITEM_CATEGORY_LABELS[it.category]})` : ""}
                    </option>
                  ))}
                </NativeSelect>
                {errors.lines?.[i]?.itemId && (
                  <FieldError>{errors.lines[i]?.itemId?.message}</FieldError>
                )}
              </Field>
              <Field>
                <FieldLabel htmlFor={`bom-qty-${i}`}>
                  한 벌당 소요량{current?.baseUnit ? ` (${current.baseUnit})` : ""}
                </FieldLabel>
                <Input
                  id={`bom-qty-${i}`}
                  type='number'
                  step='any'
                  min='0'
                  {...register(`lines.${i}.quantityPerUnit`, {
                    required: "소요량을 입력하세요.",
                    validate: (v) => Number(v) > 0 || "0보다 커야 합니다.",
                  })}
                />
                {errors.lines?.[i]?.quantityPerUnit && (
                  <FieldError>{errors.lines[i]?.quantityPerUnit?.message}</FieldError>
                )}
              </Field>
              <Field>
                <FieldLabel htmlFor={`bom-memo-${i}`}>메모</FieldLabel>
                <Input id={`bom-memo-${i}`} {...register(`lines.${i}.memo`)} />
              </Field>
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='lg:mt-6'
                onClick={() => remove(i)}
              >
                삭제
              </Button>
            </div>
          );
        })}
        {fields.length === 0 && (
          <p className='text-sm text-muted-foreground'>소요 자재가 없습니다.</p>
        )}
        <div>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={() => append(EMPTY_LINE)}
          >
            자재 추가
          </Button>
        </div>
      </div>

      <Field className='max-w-md'>
        <FieldLabel htmlFor='bom-note'>변경 사유</FieldLabel>
        <Input id='bom-note' {...register("note")} />
      </Field>

      <div>
        <Button type='submit' disabled={save.isPending || (!isDirty && !!bom)}>
          {save.isPending ? "저장 중…" : "BOM 저장 (새 버전)"}
        </Button>
      </div>
    </form>
  );
};
